import React from "react";
import { AlertTriangle, Check, Pen, Plus, Trash2, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/Card";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import Select from "@/components/ui/Select";
import CustomTooltip from "@/components/shared/CustomTooltip";
import FlowUsageStat from "./FlowUsageStat";

// The two caps a tenant admin works against on this screen, named inline
// rather than as a labelled progress bar each — a bar communicates "how full"
// no faster than the fraction beside it already does, and two of them stacked
// under the flow picker read as a dashboard widget bolted onto a settings
// control. The explanation of what's being counted moves into the tooltip:
// worth knowing once, not worth two lines of always-on caption text.

// Three of the four things a flow needs before a job can use it — same set
// and same order the server's flow_is_ready check requires. The fourth,
// "every non-terminal status leads somewhere", is not a type and is reported
// separately below. `presenceKey` points at the matching flag on the
// requiredTypePresence prop.
const REQUIRED_STATUS_TYPES = [
  { presenceKey: "hasInitial", label: "Initial" },
  { presenceKey: "hasHired", label: "Hired" },
  { presenceKey: "hasRejected", label: "Rejected" },
];

const joinWithAnd = (items) => {
  if (items.length < 2) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

/**
 * The Candidate Status page's header: which flow is being managed, what can be
 * done to it, how much of each cap is used, and whether it is usable by a job
 * yet.
 */
const FlowSummaryCard = ({
  flowOptions = [],
  flowsLoading = false,
  selectedFlow,
  selectedFlowId,
  onSelectFlow,
  flowCount = 0,
  flowLimit = 0,
  statusCount = 0,
  statusLimit = 0,
  statusesLoading = false,
  onAddFlow,
  onEditFlow,
  onDeleteFlow,
  requiredTypePresence = {
    hasInitial: false,
    hasHired: false,
    hasRejected: false,
    deadEnds: [],
  },
  className,
}) => {
  const flowLimitReached = flowCount >= flowLimit;
  // Non-terminal statuses with no allowed transition out. Readiness needs
  // this to be empty as much as it needs the three types above.
  const deadEnds = requiredTypePresence?.deadEnds ?? [];

  return (
    <Card className={cn("space-y-3 p-4", className)}>
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
        <div className="w-full sm:w-72">
          <Select
            value={selectedFlowId}
            onChange={onSelectFlow}
            options={flowOptions}
            placeholder={flowsLoading ? "Loading flows..." : "Select a flow"}
            disabled={flowsLoading}
          />
        </div>

        <CustomTooltip text="Rename this flow">
          <Button
            type="button"
            variant="outline"
            aria-label="Rename this flow"
            disabled={!selectedFlow}
            onClick={(event) => {
              // Dialog restores focus to its trigger when it closes. Clear the
              // trigger first so the hover tooltip does not remain visible.
              event.currentTarget.blur();
              if (selectedFlow) onEditFlow?.(selectedFlow);
            }}
            className="shrink-0 rounded-xl px-3"
          >
            <Pen size={14} />
          </Button>
        </CustomTooltip>

        {selectedFlow && !selectedFlow.isDefault ? (
          <CustomTooltip text="Delete this flow">
            <Button
              type="button"
              variant="outline"
              aria-label="Delete this flow"
              onClick={(event) => {
                event.currentTarget.blur();
                onDeleteFlow?.(selectedFlow);
              }}
              className="shrink-0 rounded-xl px-3 text-destructive"
            >
              <Trash2 size={14} />
            </Button>
          </CustomTooltip>
        ) : null}

        <div className="sm:ml-auto">
          <CustomTooltip text={flowLimitReached ? "Maximum number of flows reached" : ""}>
            <Button
              type="button"
              onClick={onAddFlow}
              disabled={flowLimitReached}
              className="w-full shrink-0 gap-1.5 bg-button-secondary rounded-full sm:w-auto"
            >
              <Plus size={14} />
              New Flow
            </Button>
          </CustomTooltip>
        </div>
      </div>

      {selectedFlow?.description ? (
        <p className="text-xs text-text-secondary">{selectedFlow.description}</p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <FlowUsageStat
          label="Flow capacity"
          scope="flows in this tenant"
          hint="Pipelines this tenant can define."
          used={flowCount}
          max={flowLimit}
          loading={flowsLoading}
        />
        <FlowUsageStat
          label={selectedFlow ? `Statuses in ${selectedFlow.name}` : "Statuses in selected flow"}
          scope="statuses in this flow"
          hint={
            selectedFlow
              ? `Statuses in "${selectedFlow.name}" — counted per flow, not across the tenant.`
              : "Counted per flow, not across the tenant."
          }
          used={statusCount}
          max={statusLimit}
          loading={statusesLoading}
        />
      </div>

      {selectedFlow && !selectedFlow.isReady ? (
        <Callout tone="warning" icon={AlertTriangle} size="sm">
          <div className="space-y-1">
            <p className="font-medium text-text-primary">
              This flow can't be assigned to a job yet.
            </p>

            <p className="flex flex-wrap items-center gap-1.5 text-text-secondary">
              <span>Required status types:</span>
              {REQUIRED_STATUS_TYPES.map((type) => {
                const isPresent = !!requiredTypePresence[type.presenceKey];
                return (
                  <span
                    key={type.presenceKey}
                    className={cn(
                      "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium",
                      isPresent
                        ? "border-tone-emerald/30 bg-tone-emerald-soft text-tone-emerald"
                        : "border-tone-rose/30 bg-tone-rose-soft text-tone-rose",
                    )}
                  >
                    {isPresent ? (
                      <Check size={11} aria-label="present" />
                    ) : (
                      <X size={11} aria-label="missing" />
                    )}
                    {type.label}
                  </span>
                );
              })}

              {(() => {
                const missingLabels = REQUIRED_STATUS_TYPES.filter(
                  (type) => !requiredTypePresence[type.presenceKey]
                ).map((type) => type.label);

                if (!missingLabels.length) return null;

                return (
                  <span>
                    Add the missing {joinWithAnd(missingLabels)} status
                    {missingLabels.length === 1 ? "" : "es"} to continue.
                  </span>
                );
              })()}
            </p>

            {/* The fourth thing readiness asks for. Without this line a flow
                holding all three required types shows three green ticks and
                still refuses to attach to a job, with nothing on screen
                saying why. */}
            {deadEnds.length ? (
              <p className="text-text-secondary">
                {joinWithAnd(deadEnds)} lead{deadEnds.length === 1 ? "s" : ""} nowhere
                — give {deadEnds.length === 1 ? "it" : "them"} at least one allowed
                transition, or a candidate who reaches {deadEnds.length === 1 ? "it" : "them"}{" "}
                cannot be moved on.
              </p>
            ) : null}
          </div>
        </Callout>
      ) : null}
    </Card>
  );
};

export default FlowSummaryCard;
